/**
 * Decision Engine Overview / Settings Page
 *
 * Allows configuration of the decision engine settings for a product type.
 */

import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Confirm } from '@client/components/_ui/feedback/Confirm';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { GenerateFromPdsModal, ImportDecisionEngineRulesModal } from '@client/components/claims/decision-engine';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Card, Descriptions, Form, Skeleton, Switch } from 'antd';
import { Sparkles, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';

type ConfigInput = {
    isEnabled: boolean;
    enableRedFlagDetection: boolean;
    enableLimitTracking: boolean;
    enableExclusionChecking: boolean;
    enableConditionChecking: boolean;
};

const FormItem = Form.Item<ConfigInput>;

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/$productTypeId/decision-engine/overview',
)({
    staticData: {
        metadata: {
            title: 'Overview',
        },
    },
    component: DecisionEngineOverview,
});

function DecisionEngineOverview() {
    const { productTypeId } = useParams({ strict: false });
    const { trpc, trpcUtils } = useTrpc();
    const [form] = Form.useForm<ConfigInput>();
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);

    // Fetch config
    const { data, isLoading } = trpc.claim.decisionEngine.getConfig.useQuery(
        { productTypeId: BigInt(productTypeId!) },
        { enabled: !!productTypeId },
    );

    // Upsert config mutation
    const { mutate: upsertConfig, isPending: isSaving } = trpc.claim.decisionEngine.admin.upsertConfig.useMutation({
        onSuccess() {
            message.success('Configuration saved.');
            void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    // Delete all rules mutation
    const { mutate: deleteAllRules, isPending: isDeleting } =
        trpc.claim.decisionEngine.admin.deleteAllRules.useMutation({
            onSuccess(result) {
                const { deletedCount } = result;
                const total =
                    deletedCount.productInfo +
                    deletedCount.lossCauses +
                    deletedCount.policyTriggerMappings +
                    deletedCount.exclusions +
                    deletedCount.conditions +
                    deletedCount.redFlags +
                    deletedCount.limitsAndExcesses;
                message.success(`Deleted ${total} rules successfully.`);
                void trpcUtils.claim.decisionEngine.invalidate();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    // Populate form when data loads
    useEffect(() => {
        if (data?.config) {
            form.setFieldsValue({
                isEnabled: data.config.isEnabled,
                enableRedFlagDetection: data.config.enableRedFlagDetection,
                enableLimitTracking: data.config.enableLimitTracking,
                enableExclusionChecking: data.config.enableExclusionChecking,
                enableConditionChecking: data.config.enableConditionChecking,
            });
        } else {
            // Defaults for new config
            form.setFieldsValue({
                isEnabled: false,
                enableRedFlagDetection: true,
                enableLimitTracking: true,
                enableExclusionChecking: true,
                enableConditionChecking: true,
            });
        }
    }, [data, form]);

    const onFinish = (values: ConfigInput) => {
        upsertConfig({
            productTypeId: BigInt(productTypeId!),
            isEnabled: values.isEnabled,
            enableRedFlagDetection: values.enableRedFlagDetection,
            enableLimitTracking: values.enableLimitTracking,
            enableExclusionChecking: values.enableExclusionChecking,
            enableConditionChecking: values.enableConditionChecking,
        });
    };

    if (isLoading) {
        return (
            <PageController route={Route}>
                <Space direction="vertical" size="middle" className="w-full">
                    <Skeleton active />
                    <Skeleton active />
                </Space>
            </PageController>
        );
    }

    const config = data?.config;

    // Summary counts
    const productInfoCount = config?.ProductInfo?.length ?? 0;
    const lossCauseCount = config?.LossCauses?.length ?? 0;
    const policyTriggerMappingCount = config?.PolicyTriggerMappings?.length ?? 0;
    const exclusionCount = config?.Exclusions?.length ?? 0;
    const conditionCount = config?.Conditions?.length ?? 0;
    const redFlagCount = config?.RedFlags?.length ?? 0;
    const limitsAndExcessesCount = config?.LimitsAndExcesses?.length ?? 0;

    const totalRuleCount =
        productInfoCount +
        lossCauseCount +
        policyTriggerMappingCount +
        exclusionCount +
        conditionCount +
        redFlagCount +
        limitsAndExcessesCount;

    return (
        <PageController route={Route}>
            <Space direction="vertical" size="middle" className="w-full">
                {/* Settings Form */}
                <Card title="Settings">
                    <Form<ConfigInput> form={form} layout="vertical" onFinish={onFinish} className="max-w-xl">
                        <FormItem
                            label="Enable decision engine"
                            name="isEnabled"
                            valuePropName="checked"
                            tooltip="When enabled, the AI decision engine will automatically assess new claims for this product type."
                        >
                            <Switch />
                        </FormItem>

                        <div className="mt-4 border-separator border-t pt-4">
                            <div className="mb-3 font-medium text-muted-foreground text-sm">Feature toggles</div>

                            <FormItem
                                label="Red flag detection"
                                name="enableRedFlagDetection"
                                valuePropName="checked"
                                tooltip="Enable AI-powered detection of potential red flags in claim submissions."
                            >
                                <Switch />
                            </FormItem>

                            <FormItem
                                label="Limit tracking"
                                name="enableLimitTracking"
                                valuePropName="checked"
                                tooltip="Enable tracking of sub-limits and aggregate limits for coverage decisions."
                            >
                                <Switch />
                            </FormItem>

                            <FormItem
                                label="Exclusion checking"
                                name="enableExclusionChecking"
                                valuePropName="checked"
                                tooltip="Enable checking of policy exclusions during claim assessment."
                            >
                                <Switch />
                            </FormItem>

                            <FormItem
                                label="Condition checking"
                                name="enableConditionChecking"
                                valuePropName="checked"
                                tooltip="Enable checking of policy conditions precedent during claim assessment."
                            >
                                <Switch />
                            </FormItem>
                        </div>

                        <div className="mt-6 flex justify-end border-separator border-t pt-4">
                            <Button type="primary" htmlType="submit" loading={isSaving}>
                                Save settings
                            </Button>
                        </div>
                    </Form>
                </Card>

                {/* Configuration Summary */}
                <Card
                    title="Configuration summary"
                    extra={
                        <Space>
                            {totalRuleCount > 0 && (
                                <Confirm
                                    title="Delete all rules?"
                                    description={`This will permanently delete all ${productInfoCount} product info entries, ${lossCauseCount} loss causes, ${policyTriggerMappingCount} policy trigger mappings, ${exclusionCount} exclusions, ${conditionCount} conditions, ${redFlagCount} red flags, and ${limitsAndExcessesCount} limits and excesses. This action cannot be undone.`}
                                    okText="Yes, delete all"
                                    okDanger
                                    onOk={() => {
                                        return new Promise<void>((resolve, reject) => {
                                            deleteAllRules(
                                                { productTypeId: BigInt(productTypeId!) },
                                                {
                                                    onSuccess: () => resolve(),
                                                    onError: () => reject(),
                                                },
                                            );
                                        });
                                    }}
                                >
                                    <Button type="danger" loading={isDeleting}>
                                        Delete all rules
                                    </Button>
                                </Confirm>
                            )}
                            <Button type="tertiary" icon={<Upload />} onClick={() => setIsImportModalOpen(true)}>
                                Import rules
                            </Button>
                            <Button type="tertiary" icon={<Sparkles />} onClick={() => setIsGenerateModalOpen(true)}>
                                Generate from PDS
                            </Button>
                        </Space>
                    }
                >
                    <Descriptions column={2} bordered size="small">
                        <Descriptions.Item label="Product info">{productInfoCount} configured</Descriptions.Item>
                        <Descriptions.Item label="Loss causes">{lossCauseCount} configured</Descriptions.Item>
                        <Descriptions.Item label="Policy trigger mappings">
                            {policyTriggerMappingCount} configured
                        </Descriptions.Item>
                        <Descriptions.Item label="Exclusions">{exclusionCount} configured</Descriptions.Item>
                        <Descriptions.Item label="Conditions">{conditionCount} configured</Descriptions.Item>
                        <Descriptions.Item label="Red flags">{redFlagCount} configured</Descriptions.Item>
                        <Descriptions.Item label="Limits and excesses">
                            {limitsAndExcessesCount} configured
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            {data?.isEnabled ? (
                                <span className="font-medium text-status-success">Enabled</span>
                            ) : (
                                <span className="text-muted-foreground">Disabled</span>
                            )}
                        </Descriptions.Item>
                    </Descriptions>

                    {lossCauseCount === 0 && (
                        <div className="mt-4 rounded-md bg-muted p-4">
                            <p className="text-muted-foreground text-sm">
                                No loss causes configured yet. Add loss causes to enable claim classification.
                            </p>
                        </div>
                    )}
                </Card>
            </Space>

            {/* Import Rules Modal */}
            <ImportDecisionEngineRulesModal
                open={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onSuccess={() => {
                    setIsImportModalOpen(false);
                    void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                }}
                productTypeId={BigInt(productTypeId!)}
            />

            {/* Generate from PDS Modal */}
            <GenerateFromPdsModal
                open={isGenerateModalOpen}
                onClose={() => setIsGenerateModalOpen(false)}
                onSuccess={() => {
                    setIsGenerateModalOpen(false);
                    void trpcUtils.claim.decisionEngine.getConfig.invalidate({
                        productTypeId: BigInt(productTypeId!),
                    });
                }}
                productTypeId={BigInt(productTypeId!)}
            />
        </PageController>
    );
}
